import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
  SearchRequest,
  SearchResultData,
  LotDimensionsRequest,
  MySearchRequest,
  MySearchResponse,
  MapPropertyInfo,
  REQUEST_TIMEOUT,
  LookUpData,
  SearchField,
  ResultsCountResponse,
  SearchMultiFieldLookupsRequest,
  TrendsLayerData,
  TrendsLayerRequest,
  FloodZoneData,
  SidePanelView, SidePanelTab
} from '@store/search-board/search-board.model';
import { LatLong, LotDimension, RangeInfo, RegionValue } from '@root/search/models/map.models';
import { tap, timeout } from 'rxjs/operators';
import { DataElement, Region } from '@store/user/user.model';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { select, Store } from '@ngrx/store';
import { selectSidePanelState } from '@store/search-board/search-board.selector';
import { AppState } from '@root/app-state.module';
import { selectSelectedGeos } from '@store/user/user.selector';

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  private sidePanelTabOpened: SidePanelTab;
  private countyList: Array<string>;

  constructor(private readonly httpClient: HttpClient,
              private readonly mixpanelService: MixpanelService,
              private readonly store$: Store<AppState>) {}

  public executeMySearch(searchParams: MySearchRequest): Observable<MySearchResponse> {
    return this.httpClient.post<MySearchResponse>('/api/my-search', searchParams);
  }

  public executeQuickSearch(searchParams: SearchRequest): Observable<SearchResultData> {
    return this.httpClient.post<SearchResultData>('/api/quick-search', searchParams);
  }

  public getSearchCount(searchFields: Array<SearchField>): Observable<ResultsCountResponse> {
    return this.httpClient.post<ResultsCountResponse>('/api/search-count', searchFields);
  }

  public getLotDimensions(lotDimensionsRequest: LotDimensionsRequest): Observable<Array<LotDimension>> {
    this.mixPanelServiceTracking('Search - Parcel Dimensions Click', lotDimensionsRequest.parcelId);
    return this.httpClient.post<Array<LotDimension>>('/api/get-lot-dimensions', lotDimensionsRequest);
  }

  public getPropertyInformation({latitude, longitude}: LatLong, displayFields?: string): Observable<MapPropertyInfo> {
    const params = new HttpParams({
      fromObject: { latitude: `${latitude}`, longitude: `${longitude}`, displayFields: displayFields ? displayFields : null }
    });

    return this.httpClient.get<MapPropertyInfo>('/api/property-information', { params })
      .pipe(
        timeout(REQUEST_TIMEOUT)
      );
  }

  public getLegendData(regionValue: RegionValue): Observable<Array<RangeInfo>> {
    return this.httpClient.post<Array<RangeInfo>>('/api/map-legend', regionValue);
  }

  public getTrendsLayerData(trendsLayerRequest: TrendsLayerRequest): Observable<Array<TrendsLayerData>> {
    return this.httpClient.post<Array<TrendsLayerData>>('/api/reports/market-trends-popup', trendsLayerRequest);
  }

  public getFloodZoneData(floodZoneRequest: TrendsLayerRequest): Observable<Array<FloodZoneData>> {
    return this.httpClient.post<Array<FloodZoneData>>('/api/reports/flood-map-info-popup', floodZoneRequest);
  }

  public getLookUps(dataElements: Array<DataElement>, selectedRegions: Array<Region>): Observable<{[key: string]: Array<LookUpData>}> {
    return this.httpClient.post<{[key: string]: Array<LookUpData>}>('/api/preference/get-lookup', {
      searchTemplates: [
        {dataElementList: dataElements}
      ],
      selectedRegions: selectedRegions
    });
  }

  public getLookupsForSearchMultiField(searchData: SearchMultiFieldLookupsRequest): Observable<Array<string>> {
    return this.httpClient.post<Array<string>>('/api/preference/get-lookup-by-token', searchData);
  }

  private mixPanelServiceTracking(eventId: string, propertyId: string) {
    this.store$
      .pipe(
        select(selectSidePanelState)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });

    this.store$.select(selectSelectedGeos).subscribe((result) => this.countyList = result.map((x) => x.fips));

    this.mixpanelService.track(eventId, {
      propertyIdentifier: propertyId,
      countyList: this.countyList,
      searchType: this.sidePanelTabOpened
    });
  }

  public searchAddress(address: string): Observable<Array<string>> {
    if(sessionStorage.getItem(address)){
      return of(JSON.parse(sessionStorage.getItem(address)));
    } else {
      return this.httpClient.get<Array<string>>('/api/get-type-ahead?address=' + address).pipe(tap((value) => { sessionStorage.setItem(address, JSON.stringify(value)) }));
    }
  }
}
